/**
 * DSD 테이블 셀 + 텍스트 블록 컴포넌트
 *
 * Figma: Design System 0.1 > DSD Table
 *
 * - DsdHeaderCell: Type = Header (DecorateInteraction strong/neutral)
 * - DsdCellBase: 본문 셀 베이스 (DecorateInteraction light/neutral 고정)
 * - DsdCell: DSD-DSD 대사 도메인 셀 (합계/단수 + 변경 상태)
 * - DsdTextBlock: NarrativeBlock (텍스트 문단 블록, 변경 상태 + indicator)
 */

import { cn } from "@/lib/utils";
import { DecorateInteraction } from "./decorate/interaction/decorate-interaction";

// ============================================
// 공통 타입 + 변경 상태 유틸
// ============================================

export type SumStatus = "default" | "sum-match" | "sum-mismatch";
export type ChangeStatus = "default" | "deleted" | "modified" | "inserted";

/** changeStatus → 배경색 */
const changeBgMap: Record<ChangeStatus, string> = {
  default: "bg-background-base",
  deleted: "bg-red-200",
  modified: "bg-indigo-200",
  inserted: "bg-lime-200",
};

/** cellIndicator 색상 결정 */
const indicatorColorMap: Record<string, string> = {
  deleted: "bg-red-500",
  modified: "bg-indigo-500",
  inserted: "bg-lime-500",
  "sum-mismatch": "bg-primary-base",
};

/** 왼쪽 4px 변경 상태 띠지 */
const CellIndicator = ({
  status,
  className,
}: {
  status: string;
  className?: string;
}) => {
  const color = indicatorColorMap[status];
  if (!color) return null;
  return <div className={cn("absolute left-0 w-1", color, className)} />;
};

// ============================================
// 셀 선택 (Selection Overlay)
// ============================================

export type SelectionColor =
  | "primary"
  | "red"
  | "lime"
  | "indigo"
  | "orange"
  | "slate";

const selectionColorMap: Record<
  SelectionColor,
  { border: string; bg: string }
> = {
  primary: { border: "border-primary-base", bg: "bg-primary-base" },
  red: { border: "border-red-600", bg: "bg-red-600" },
  lime: { border: "border-lime-600", bg: "bg-lime-600" },
  indigo: { border: "border-indigo-600", bg: "bg-indigo-600" },
  orange: { border: "border-orange-500", bg: "bg-orange-500" },
  slate: { border: "border-slate-500", bg: "bg-slate-500" },
};

/** 셀 선택 시 inset outline + 상단 indicator tab */
const SelectionOverlay = ({
  color,
  label,
}: {
  color: SelectionColor;
  label: string;
}) => {
  const colors = selectionColorMap[color];
  return (
    <>
      <div
        className={cn(
          "absolute inset-0 pointer-events-none border-2 z-10",
          colors.border,
        )}
      />
      <div
        className={cn(
          "absolute bottom-full left-0 z-10 px-1 py-0.5 rounded-t-[4px] label-2-medium text-white whitespace-nowrap",
          colors.bg,
        )}
      >
        {label}
      </div>
    </>
  );
};

/** text-align → flex justify-content 매핑 */
const textAlignToJustify: Record<string, string> = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

/** style prop에서 textAlign을 읽어 flex justify 클래스 반환 */
const resolveJustifyClass = (style?: React.CSSProperties): string =>
  textAlignToJustify[(style?.textAlign as string) ?? ""] ?? "";

/** ghost 셀 빗금 패턴 (Figma: Status=Alignment) — changeStatus와 조합 가능 */
const ghostStripeStyle: React.CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)",
};

// ============================================
// DsdHeaderCell
// ============================================

export interface DsdHeaderCellProps
  extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** 변경 상태 */
  changeStatus?: ChangeStatus;
  /** 왼쪽 Cell Indicator 띠지 표시 */
  cellIndicator?: boolean;
  /** 정렬용 ghost 셀 (빗금 패턴) */
  ghost?: boolean;
  /** 선택 시 outline + indicator 색상 (전달 시 overlay 렌더) */
  selectionColor?: SelectionColor;
  /** 선택 시 indicator 탭 라벨 (전달 시 overlay 렌더) */
  selectionLabel?: string;
  /** 포커스 상태 */
  focused?: boolean;
  /** pair 셀 단순 선택 border class (전달 시 border 렌더) */
  plainSelectionBorder?: string;
  plainSelectionBorderWidth?: "thin" | "thick";
  children?: React.ReactNode;
  className?: string;
}

export const DsdHeaderCell = ({
  changeStatus = "default",
  cellIndicator = false,
  ghost = false,
  selectionColor,
  selectionLabel,
  focused = false,
  plainSelectionBorder,
  plainSelectionBorderWidth = "thick",
  children,
  className,
  style,
  ...rest
}: DsdHeaderCellProps) => {
  const bgClass =
    changeStatus === "default" ? "bg-neutral-200" : changeBgMap[changeStatus];
  const content = ghost && !children ? "\u00A0" : children;

  const hasOverlay = !!selectionColor && !!selectionLabel;
  const selectionEl = hasOverlay && (
    <SelectionOverlay color={selectionColor!} label={selectionLabel!} />
  );
  const overflowStyle = hasOverlay
    ? { overflow: "visible" as const }
    : undefined;
  const mergedStyle = ghost
    ? { ...ghostStripeStyle, ...style, ...overflowStyle }
    : overflowStyle
      ? { ...style, ...overflowStyle }
      : style;

  return (
    <th
      className={cn(
        "relative min-w-[80px] p-0 h-px",
        bgClass,
        "border border-line-normal",
        className,
      )}
      style={mergedStyle}
      {...rest}
    >
      {changeStatus !== "default" ? (
        <DecorateInteraction asChild variant="strong" color="neutral">
          <div
            className={cn(
              "relative flex gap-1 rounded-none p-2 label-2-medium text-label-strong h-full",
              resolveJustifyClass(mergedStyle),
            )}
          >
            {content}
            {cellIndicator && (
              <CellIndicator status={changeStatus} className="top-0 bottom-0" />
            )}
          </div>
        </DecorateInteraction>
      ) : (
        <div
          className={cn(
            "relative flex gap-1 rounded-none p-2 label-2-medium text-label-strong h-full",
            resolveJustifyClass(mergedStyle),
          )}
        >
          {content}
        </div>
      )}
      {focused && (
        <div className="absolute inset-0 pointer-events-none bg-neutral-800/[.0675]" />
      )}
      {selectionEl}
      {plainSelectionBorder && (
        <div
          className={cn(
            "absolute inset-0 pointer-events-none z-10",
            plainSelectionBorderWidth === "thin" ? "border" : "border-2",
            plainSelectionBorder,
          )}
        />
      )}
    </th>
  );
};

// ============================================
// DsdCellBase
// ============================================

export interface DsdCellBaseProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** 왼쪽 슬롯 (e.g. 칩, 아이콘) */
  left?: React.ReactNode;
  /** 오른쪽 슬롯 */
  right?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

export const DsdCellBase = ({
  left,
  right,
  children,
  className,
  ...rest
}: DsdCellBaseProps) => {
  return (
    <td
      className={cn(
        "min-w-[80px] p-0",
        "bg-background-base",
        "border border-line-normal",
        className,
      )}
      {...rest}
    >
      <DecorateInteraction asChild variant="light" color="neutral">
        <div className="flex items-center gap-1 rounded-none p-2 label-2-medium">
          {left}
          <span className="flex-1 min-w-0">{children}</span>
          {right}
        </div>
      </DecorateInteraction>
    </td>
  );
};

// ============================================
// 칩
// ============================================

/** 단수차이 칩 — Σ 아이콘 + 숫자 (e.g. "-3", "+88"). IXD-DSD 대사의 rounded 셀도 재사용. */
export const SumDiffChip = ({
  children,
  variant,
}: {
  children: React.ReactNode;
  variant: "dark" | "light";
}) => (
  <span
    data-chip-type="sum-diff"
    className={cn(
      "inline-flex items-center gap-0.5 px-1 py-0.5 rounded caption-2-medium shrink-0",
      variant === "dark"
        ? "bg-primary-base text-static-white"
        : "bg-slate-200 text-label-strong",
    )}
  >
    {children}
  </span>
);

/** 합계 일치 칩 — "합계" 라벨 */
const SumMatchChip = () => (
  <span
    data-chip-type="sum-match"
    className="inline-flex items-center px-1 py-0.5 rounded caption-2-medium bg-orange-500 text-static-white shrink-0"
  >
    합계
  </span>
);

// ============================================
// DsdCell
// ============================================

export interface DsdCellProps
  extends React.TdHTMLAttributes<HTMLTableCellElement> {
  /** 합계 상태 */
  sumStatus?: SumStatus;
  /** 변경 상태 */
  changeStatus?: ChangeStatus;
  /** 왼쪽 Cell Indicator 띠지 표시 */
  cellIndicator?: boolean;
  /** Indicator 색상 우선순위: "sum"이면 합계불일치 우선, "change"이면 변경 상태 우선 */
  indicatorPriority?: "sum" | "change";
  /** 정렬용 ghost 셀 (빗금 패턴) */
  ghost?: boolean;
  /** 단수차이 칩 라벨 (e.g. "-3", "+88") */
  sumDiffLabel?: string;
  /** false이면 합계/단수차이 칩 모두 숨김 (기본 true) */
  showSumChips?: boolean;
  /** 단수차이 칩 variant 외부 제어 (미지정 시 내부 로직) */
  sumDiffChipVariant?: "dark" | "light";
  /** 선택 상태 */
  selected?: boolean;
  /** 선택 시 outline + indicator 색상 */
  selectionColor?: SelectionColor;
  /** 선택 시 indicator 탭 라벨 */
  selectionLabel?: string;
  /** 합계 자식 하이라이트 */
  highlighted?: boolean;
  /** 포커스 상태 (디자인 시스템 focused 배경색 적용) */
  focused?: boolean;
  /** pair 셀 단순 선택 border class (전달 시 border 렌더) */
  plainSelectionBorder?: string;
  plainSelectionBorderWidth?: "thin" | "thick";
  children?: React.ReactNode;
  className?: string;
}

export const DsdCell = ({
  sumStatus = "default",
  changeStatus = "default",
  cellIndicator = false,
  indicatorPriority = "sum",
  ghost = false,
  sumDiffLabel,
  showSumChips = true,
  sumDiffChipVariant: sumDiffChipVariantProp,
  selected = false,
  selectionColor,
  selectionLabel,
  highlighted = false,
  focused = false,
  plainSelectionBorder,
  plainSelectionBorderWidth = "thick",
  children,
  className,
  style,
  ...rest
}: DsdCellProps) => {
  const isSumMismatch = sumStatus === "sum-mismatch";
  const isSumMatch = sumStatus === "sum-match";
  const content = ghost && !children ? "\u00A0" : children;

  // ── 배경색 (selected/highlighted 시 override) ──
  // 합계불일치 + 변경 → 변경 배경, 합계불일치 단독 → dark primary 배경
  const bgClass = highlighted
    ? "bg-orange-200"
    : (selected || plainSelectionBorder) && isSumMatch
      ? "bg-background-base"
      : isSumMismatch && changeStatus === "default"
        ? "bg-primary-base"
        : changeBgMap[changeStatus];

  // ── 왼쪽 칩 ──
  const resolvedChipVariant =
    sumDiffChipVariantProp ??
    (isSumMismatch && changeStatus === "default" ? "light" : "dark");
  const leftChips = showSumChips ? (
    <>
      {isSumMatch && <SumMatchChip />}
      {sumDiffLabel && (
        <SumDiffChip variant={resolvedChipVariant}>{sumDiffLabel}</SumDiffChip>
      )}
    </>
  ) : null;

  // ── Cell Indicator (4px left stripe) ──
  const indicatorKey =
    indicatorPriority === "change"
      ? changeStatus !== "default"
        ? changeStatus
        : isSumMismatch
          ? "sum-mismatch"
          : null
      : isSumMismatch
        ? "sum-mismatch"
        : changeStatus !== "default"
          ? changeStatus
          : null;
  const indicatorEl = cellIndicator && indicatorKey && (
    <CellIndicator status={indicatorKey} className="top-0 bottom-0" />
  );

  const hasOverlay = !!selectionColor && !!selectionLabel;
  const selectionEl = hasOverlay && (
    <SelectionOverlay color={selectionColor!} label={selectionLabel!} />
  );

  // inline style로 overflow 지정 — ROUNDED_TABLE의 overflow-hidden(모서리 radius 클리핑)보다
  // class specificity가 낮아 씹히는 문제 방지
  const overflowStyle = hasOverlay
    ? { overflow: "visible" as const }
    : undefined;
  const mergedStyle = ghost
    ? { ...ghostStripeStyle, ...style, ...overflowStyle }
    : overflowStyle
      ? { ...style, ...overflowStyle }
      : style;

  // ── 합계 불일치 단독(변경 없음): dark bg + DecorateInteraction strong/white ──
  // highlighted(합계 children 하이라이트) 시에는 bg-orange-200 + 검정 글자로 표시해야 하므로 일반 분기로 넘김
  if (isSumMismatch && changeStatus === "default" && !highlighted) {
    return (
      <td
        className={cn(
          "relative min-w-[80px] p-0 h-px",
          bgClass,
          "border border-line-normal",
          className,
        )}
        style={mergedStyle}
        {...rest}
      >
        <DecorateInteraction asChild variant="strong" color="white">
          <div
            className={cn(
              "flex items-center gap-1 rounded-none p-2 label-2-medium text-static-white h-full",
              resolveJustifyClass(mergedStyle),
            )}
          >
            {leftChips}
            <span className="flex-1 min-w-0">{content}</span>
          </div>
        </DecorateInteraction>
        {focused && (
          <div className="absolute inset-0 pointer-events-none bg-white/[.135]" />
        )}
        {indicatorEl}
        {selectionEl}
        {plainSelectionBorder && (
          <div
            className={cn(
              "absolute inset-0 pointer-events-none z-10",
              plainSelectionBorderWidth === "thin" ? "border" : "border-2",
              plainSelectionBorder,
            )}
          />
        )}
      </td>
    );
  }

  // ── 합계 일치: bg 색상 전환 hover ──
  if (isSumMatch) {
    return (
      <td
        className={cn(
          "relative min-w-[80px] p-0 h-px",
          bgClass,
          "border border-line-normal",
          className,
        )}
        style={mergedStyle}
        {...rest}
      >
        <div
          className={cn(
            "flex items-center gap-1 p-2 h-full transition-colors cursor-pointer label-2-medium text-label-normal",
            resolveJustifyClass(mergedStyle),
            focused && !(selected || plainSelectionBorder)
              ? "bg-orange-500"
              : bgClass,
            !(selected || plainSelectionBorder) &&
              !focused &&
              "hover:bg-orange-200 active:bg-orange-500",
          )}
        >
          {!(selected || plainSelectionBorder) && leftChips}
          {/* data-sum-number: dsd-dsd-compare 의 숫자 잘림 보정 effect 가 찾는 마커 */}
          <span data-sum-number className="flex-1 min-w-0">
            {content}
          </span>
        </div>
        {indicatorEl}
        {selectionEl}
        {plainSelectionBorder && (
          <div
            className={cn(
              "absolute inset-0 pointer-events-none z-10",
              plainSelectionBorderWidth === "thin" ? "border" : "border-2",
              plainSelectionBorder,
            )}
          />
        )}
      </td>
    );
  }

  // ── 기본: 변경 있으면 DecorateInteraction light/neutral, 없으면 인터랙션 제거 ──
  return (
    <td
      className={cn(
        "relative min-w-[80px] p-0 h-px",
        bgClass,
        "border border-line-normal",
        className,
      )}
      style={mergedStyle}
      {...rest}
    >
      {changeStatus !== "default" ? (
        <DecorateInteraction asChild variant="light" color="neutral">
          <div
            className={cn(
              "flex items-center gap-1 rounded-none p-2 label-2-medium text-label-strong h-full",
              resolveJustifyClass(mergedStyle),
            )}
          >
            {leftChips}
            <span className="flex-1 min-w-0">{content}</span>
          </div>
        </DecorateInteraction>
      ) : (
        <div
          className={cn(
            "flex items-center gap-1 rounded-none p-2 label-2-medium text-label-strong h-full",
            resolveJustifyClass(mergedStyle),
          )}
        >
          {leftChips}
          <span className="flex-1 min-w-0">{content}</span>
        </div>
      )}
      {focused && (
        <div className="absolute inset-0 pointer-events-none bg-neutral-800/[.0675]" />
      )}
      {indicatorEl}
      {selectionEl}
      {plainSelectionBorder && (
        <div
          className={cn(
            "absolute inset-0 pointer-events-none z-10",
            plainSelectionBorderWidth === "thin" ? "border" : "border-2",
            plainSelectionBorder,
          )}
        />
      )}
    </td>
  );
};

// ============================================
// DsdTextBlock (NarrativeBlock)
// ============================================

export interface DsdTextBlockProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** 변경 상태 */
  changeStatus?: ChangeStatus;
  /** 왼쪽 Cell Indicator 띠지 표시 */
  cellIndicator?: boolean;
  children?: React.ReactNode;
  className?: string;
}

export const DsdTextBlock = ({
  changeStatus = "default",
  cellIndicator = false,
  children,
  className,
  ...rest
}: DsdTextBlockProps) => {
  const indicatorEl = cellIndicator && changeStatus !== "default" && (
    <CellIndicator status={changeStatus} className="top-1 bottom-1" />
  );

  return (
    <div className={cn("relative p-0", className)} {...rest}>
      <div className="flex flex-col gap-3 rounded-none px-2 py-1 label-2-medium text-label-normal">
        {children}
      </div>
      {indicatorEl}
    </div>
  );
};
